/**
 * Module Photos (mode classique) — Phase 1, incrément 9.
 *
 * Port de `vPhotos` du proto (l. 1799+) :
 *   - Section « Toutes les photos » : grille carrée, fallback emoji
 *     pour les cellules sans `img`, croix de suppression au hover.
 *   - Section « Albums » : grille de cartes album avec thumbnail
 *     (couverture = 1re photo de l'album), nom, count, et 3 actions
 *     inline : ✏️ renommer, 📤 partager, 🗑 supprimer.
 *   - Modal d'ajout : input fichier image (FileReader → data: URL),
 *     légende libre, sélecteur d'album (création à la volée).
 *   - Bouton ✨ IA : groupe les photos sans album dans un album
 *     « Récents » (interprétation explicite de l'audit §2.2 photos —
 *     l'IA opaque du proto est documentée et déterministe ici).
 *
 * Toutes les chaînes UI passent par `t(key)`.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { t, tn } from '../../i18n/index.js';
import {
  addAlbum,
  addPhoto,
  getCurrentHome,
  PHOTO_IDB_SENTINEL,
  removeAlbum,
  removePhoto,
  renameAlbum,
  setPhotoAlbum,
} from '../../stores/homes.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { getPhotoUrlNow } from '../../stores/photoUrls.js';
import type { Album, ClassicHomeData, Photo } from '@nestor/shared-types';
import { modalTemplate } from '../../ui/Modal.js';
import { share } from '../../lib/share.js';
import { showToast } from '../../ui/Toast.js';
import { confirmDialog, promptDialog } from '../../ui/Dialog.js';
import { editorialHero } from '../../ui/Hero.js';

/**
 * Résout l'URL effective d'une photo pour l'affichage.
 *
 * - `photo.img == null` : pas de visuel (fallback emoji dans la View).
 * - `photo.img === '[idb]'` (Phase 2.2) : le blob réel vit en IDB, on
 *   passe par le cache mémoire `getPhotoUrlNow(id)`. Retourne `null`
 *   pendant l'hydratation (premier render après reload) — le module
 *   `photoUrls` déclenchera un `bumpUi()` quand le blob sera prêt.
 * - Autre valeur (URL data:, blob:, http:) : utilisée telle quelle.
 *   Cas legacy (avant 2.2) ou import depuis un test direct.
 */
function resolvePhotoSrc(photo: Photo): string | null {
  if (photo.img == null) return null;
  if (photo.img === PHOTO_IDB_SENTINEL) return getPhotoUrlNow(photo.id);
  return photo.img;
}

const MODAL_PHOTO = 'photos:add';

// ── Sub-templates ─────────────────────────────────────────────────────

function photoCellTemplate(homeId: string, photo: Photo): TemplateResult {
  const src = resolvePhotoSrc(photo);
  return html`
    <div class=${styles.photoCell} data-testid="photo-cell" data-photo-id=${photo.id}>
      ${src
        ? html`<img src=${src} alt=${photo.caption ?? ''} />`
        : html`<span class=${styles.fallback}>${photo.caption?.slice(0, 2) ?? '·'}</span>`}
      <button
        type="button"
        class=${styles.photoDelete}
        aria-label=${t('remove')}
        data-testid=${`photo-remove-${photo.id}`}
        @click=${(): void => {
          removePhoto(homeId, photo.id);
        }}
      >
        ✕
      </button>
    </div>
  `;
}

function photosCard(homeId: string, photos: readonly Photo[]): TemplateResult {
  return html`
    <div class=${styles.card} data-testid="photos-section">
      <div class=${styles.cardTitle}>
        <span>${t('all_photos')}</span>
        <div class=${styles.cardTitleActions}>
          <button
            type="button"
            class=${styles.btnGhost}
            data-testid="btn-add-photo"
            @click=${(): void => openModal(MODAL_PHOTO)}
          >
            + ${t('add')}
          </button>
        </div>
      </div>
      ${photos.length === 0
        ? html`<div class=${styles.empty} data-testid="photos-empty">
            <div class=${styles.emptyIcon}>${Icon(Icons.image, 26)}</div>
            <div class=${styles.emptyTitle}>${t('no_photos')}</div>
            <div class=${styles.emptySub}>${t('no_photos_sub')}</div>
          </div>`
        : html`<div class=${styles.photoGrid} data-testid="photos-grid">
            ${photos.map((p) => photoCellTemplate(homeId, p))}
          </div>`}
    </div>
  `;
}

async function shareAlbum(album: Album, count: number): Promise<void> {
  const result = await share({
    title: album.name,
    text: `${album.name} — ${count.toString()} ${tn('photo_count', count)}`,
  });
  if (result.via === 'copied') showToast({ message: t('share_copied'), kind: 'success' });
  if (result.via === 'unsupported') showToast({ message: t('share_unsupported'), kind: 'info' });
}

function renameAlbumPrompt(homeId: string, album: Album): void {
  void promptDialog({ title: t('rename'), defaultValue: album.name }).then((next) => {
    if (next !== null) renameAlbum(homeId, album.id, next);
  });
}

function deleteAlbumPrompt(homeId: string, album: Album, count: number): void {
  void confirmDialog({
    title: t('home_remove'),
    message: `${album.name} (${count.toString()})`,
    confirmLabel: t('remove'),
    danger: true,
  }).then((ok) => {
    if (ok) removeAlbum(homeId, album.id);
  });
}

function albumCardTemplate(homeId: string, album: Album, photos: readonly Photo[]): TemplateResult {
  const albumPhotos = photos.filter((p) => p.album === album.name);
  // Cover = première photo de l'album avec une image résolvable. Le
  // round-trip IDB potentiel passe par le même cache que les cellules
  // (donc 0 re-fetch après le premier render).
  const cover = albumPhotos.map((p) => resolvePhotoSrc(p)).find((src): src is string => !!src);
  return html`
    <div class=${styles.albumCard} data-testid="album-card" data-album-id=${album.id}>
      <div class=${styles.albumThumb}>
        ${cover ? html`<img src=${cover} alt=${album.name} />` : null}
        <span>${album.name}</span>
      </div>
      <div class=${styles.albumInfo}>
        <div class=${styles.albumName}>${album.name}</div>
        <div class=${styles.albumCount}>
          ${albumPhotos.length} ${tn('photo_count', albumPhotos.length)}
        </div>
        <div class=${styles.albumActions}>
          <button
            type="button"
            class=${styles.actionEdit}
            data-testid=${`album-rename-${album.id}`}
            @click=${(): void => renameAlbumPrompt(homeId, album)}
          >
            ${Icon(Icons.pencil, 13)} ${t('rename')}
          </button>
          <button
            type="button"
            class=${styles.actionShare}
            data-testid=${`album-share-${album.id}`}
            @click=${(): void => {
              void shareAlbum(album, albumPhotos.length);
            }}
          >
            ${Icon(Icons.share, 13)} ${t('share')}
          </button>
          <button
            type="button"
            class=${styles.actionDelete}
            data-testid=${`album-remove-${album.id}`}
            @click=${(): void => deleteAlbumPrompt(homeId, album, albumPhotos.length)}
          >
            ${Icon(Icons.trash, 14)}
          </button>
        </div>
      </div>
    </div>
  `;
}

function albumsCard(
  homeId: string,
  albums: readonly Album[],
  photos: readonly Photo[],
): TemplateResult {
  return html`
    <div class=${styles.card} data-testid="albums-section">
      <div class=${styles.cardTitle}>
        <span>${t('albums')}</span>
        <div class=${styles.cardTitleActions}>
          <button
            type="button"
            class=${styles.btnGhost}
            data-testid="btn-ai-album"
            title=${t('auto_album')}
            @click=${(): void => {
              // Item 10 : albums AUTOMATIQUES par métadonnées — les
              // photos sans album sont classées par mois de prise de
              // vue (« Juillet 2026 »), album créé au besoin. La Map
              // locale suit les albums créés en cours de boucle (la
              // liste `albums` du rendu est figée, et addAlbum refuse
              // les doublons de nom).
              const orphans = photos.filter((p) => !p.album);
              if (orphans.length === 0) return;
              const lang = getLang();
              const byName = new Map(albums.map((a) => [a.name, a] as const));
              for (const p of orphans) {
                const name = monthYearLabel(p.takenAt ?? Date.now(), lang);
                if (!name) continue;
                const album = byName.get(name) ?? addAlbum(homeId, name);
                if (!album) continue;
                byName.set(name, album);
                setPhotoAlbum(homeId, p.id, album.name);
              }
              showToast({ message: t('auto_album_done'), kind: 'success' });
            }}
          >
            ${t('auto_album')}
          </button>
        </div>
      </div>
      ${albums.length === 0
        ? html`<div class=${styles.empty}>
            <div class=${styles.emptySub}>${t('no_albums_sub')}</div>
          </div>`
        : html`<div class=${styles.albumGrid} data-testid="albums-grid">
            ${albums.map((a) => albumCardTemplate(homeId, a, photos))}
          </div>`}
    </div>
  `;
}

// ── Modal d'ajout ─────────────────────────────────────────────────────

let pendingFile: { dataUrl: string; name: string; takenAt: number } | null = null;

function clearPending(): void {
  pendingFile = null;
}

function onPickFile(e: Event): void {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (): void => {
    if (typeof reader.result !== 'string') return;
    // Item 10 : vraie date de prise de vue — EXIF si le JPEG en porte
    // une, sinon date du fichier (les albums auto s'appuient dessus).
    const takenAt = exifDateFromDataUrl(reader.result) ?? file.lastModified ?? Date.now();
    pendingFile = { dataUrl: reader.result, name: file.name, takenAt };
    // Force re-render via le tick du ui store
    // (le composer-tick n'est pas exposé ici ; on passe par closeModal
    // puis openModal serait moche → on import bumpUi).
    bumpUi();
  };
  reader.readAsDataURL(file);
}

import { bumpUi } from '../../stores/ui.js';
import { exifDateFromDataUrl } from '../../lib/exifDate.js';
import { monthYearLabel } from '../../lib/date.js';
import { getLang } from '../../i18n/index.js';

function addPhotoFormTemplate(homeId: string, albums: readonly Album[]): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!pendingFile) return;
    const form = e.target as HTMLFormElement;
    const caption = (form.elements.namedItem('caption') as HTMLInputElement).value;
    const album = (form.elements.namedItem('album') as HTMLSelectElement).value;
    addPhoto(homeId, {
      img: pendingFile.dataUrl,
      takenAt: pendingFile.takenAt,
      ...(caption.trim() ? { caption } : {}),
      ...(album ? { album } : {}),
    });
    clearPending();
    closeModal();
  };
  return html`
    <form class=${styles.form} @submit=${onSubmit} data-testid="photo-form">
      <div class=${styles.field}>
        <label for="ph-file">${t('attach_image')}</label>
        <input
          id="ph-file"
          type="file"
          name="file"
          accept="image/*"
          required
          data-testid="ph-file"
          @change=${onPickFile}
        />
      </div>
      ${pendingFile
        ? html`<div class=${styles.previewWrap}>
            <img src=${pendingFile.dataUrl} alt=${pendingFile.name} />
            <small>${pendingFile.name}</small>
          </div>`
        : null}
      <div class=${styles.field}>
        <label for="ph-caption">${t('caption_label')} ${t('album_optional')}</label>
        <input
          id="ph-caption"
          name="caption"
          type="text"
          maxlength="100"
          data-testid="ph-caption"
        />
      </div>
      <div class=${styles.field}>
        <label for="ph-album">${t('album_label')} ${t('album_optional')}</label>
        <select id="ph-album" name="album" data-testid="ph-album">
          <option value="">${t('no_album')}</option>
          ${albums.map((a) => html`<option value=${a.name}>${a.name}</option>`)}
        </select>
      </div>
      <div class=${styles.formActions}>
        <button
          type="button"
          class=${styles.cancel}
          @click=${(): void => {
            clearPending();
            closeModal();
          }}
        >
          ${t('cancel')}
        </button>
        <button
          type="submit"
          class=${styles.submit}
          ?disabled=${!pendingFile}
          data-testid="ph-submit"
        >
          ${t('save')}
        </button>
      </div>
    </form>
  `;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderPhotosView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'classic') {
    return html`<section data-testid="view-photos" class=${styles.view}></section>`;
  }
  const data = home.data as ClassicHomeData;
  const photos = data.photos;
  const albums = data.albums;
  const modal = getModal();

  return html`
    <section data-testid="view-photos" class=${classMap({ [styles.view!]: true })}>
      ${editorialHero({
        badge: { mode: 'classic', label: home.name },
        dateLine: t('photos_hero_dateline'),
        title: t('photos_hero_title'),
        accent: t('photos_hero_accent'),
      })}
      ${photosCard(home.id, photos)} ${albumsCard(home.id, albums, photos)}
      ${modal === MODAL_PHOTO
        ? modalTemplate({
            title: t('add_photo'),
            content: addPhotoFormTemplate(home.id, albums),
            onClose: (): void => {
              clearPending();
              closeModal();
            },
            closeLabel: t('close'),
            testid: 'modal-photo',
          })
        : null}
    </section>
  `;
}
